import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, ExternalLink, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { SeverityBadge, StatusBadge } from './badges';
import { formatDateTime } from './format';
import { AdminAgentIdentity, apiService, ErrorEvent } from '../../../services/api';
import { copyToClipboard } from '../../../utils/clipboard';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { buildAgentPortalPath, getCustomerPortalBaseUrl } from '../../../utils/portalLink';

export interface ErrorEventDetailDialogProps {
  event: ErrorEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAcknowledge: (event: ErrorEvent) => void;
  onResolve: (event: ErrorEvent) => void;
  actionPending: boolean;
}

/** Welcher Portal-Link gerade Impersonation + Ziel auflöst (Spinner-Zustand). */
type PortalLinkTarget = 'customer' | 'agent';

/** Detail-Modal für ein einzelnes Fehler-Event — Meta-Daten, Freitexte, Quittieren/Lösen. */
export function ErrorEventDetailDialog({
  event,
  open,
  onOpenChange,
  onAcknowledge,
  onResolve,
  actionPending,
}: ErrorEventDetailDialogProps) {
  const [linkPending, setLinkPending] = useState<PortalLinkTarget | null>(null);

  // Aufgelöste Anzeige-Namen zu den UUIDs — bleiben leer, wenn die Auflösung fehlschlägt.
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [agentIdentity, setAgentIdentity] = useState<AdminAgentIdentity | null>(null);

  const customerId = event?.customerId ?? null;
  const agentId = event?.agentId ?? null;

  useEffect(() => {
    setCustomerName(null);
    setAgentIdentity(null);
    if (!open) return;
    let cancelled = false;
    if (customerId) {
      apiService
        .getCustomer(customerId)
        .then((customer) => {
          if (!cancelled) setCustomerName(customer.name);
        })
        .catch(() => {
          // Name bleibt leer — der UUID-Link funktioniert trotzdem.
        });
    }
    if (agentId) {
      apiService
        .getAgentByIdAdmin(agentId)
        .then((agent) => {
          if (!cancelled) setAgentIdentity(agent);
        })
        .catch(() => {
          // Name bleibt leer — der UUID-Link löst beim Klick selbst auf.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open, customerId, agentId]);

  if (!event) return null;

  /**
   * Öffnet das Customer-Portal als der betroffene Kunde (Impersonation, neuer Tab).
   * Gleiches Muster wie die Quick-Impersonation in der System-Events-Tabelle.
   */
  const handleOpenCustomerPortal = async () => {
    if (!customerId || linkPending) return;
    setLinkPending('customer');
    try {
      const impersonation = await apiService.startImpersonation(
        customerId,
        `Admin impersonation from error event ${event.errorIdentifier}`,
      );
      const url = `${getCustomerPortalBaseUrl()}/?impersonation_token=${encodeURIComponent(impersonation.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Customer-Portal geöffnet', {
        description: 'Impersonation gestartet — in neuem Tab geöffnet.',
        duration: 4000,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Customer-Portal konnte nicht geöffnet werden'));
    } finally {
      setLinkPending(null);
    }
  };

  /**
   * Öffnet die Agent-Detailseite im Customer-Portal (Impersonation, neuer Tab).
   * Nutzt die bereits aufgelöste Agent-Identität (Customer + Bot-Typ), falls vorhanden —
   * gleiches Muster wie die Portal-Link-Funktion im Header.
   */
  const handleOpenAgentPortal = async () => {
    if (!agentId || linkPending) return;
    setLinkPending('agent');
    try {
      const agent = agentIdentity ?? (await apiService.getAgentByIdAdmin(agentId));
      const impersonation = await apiService.startImpersonation(
        agent.customerId,
        `Admin impersonation from error event ${event.errorIdentifier} for agent ${agent.name}`,
      );
      const url =
        `${getCustomerPortalBaseUrl()}${buildAgentPortalPath(agent.type, agent.id)}` +
        `?impersonation_token=${encodeURIComponent(impersonation.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Agent geöffnet', {
        description: `Impersonation für ${agent.name} gestartet — in neuem Tab geöffnet.`,
        duration: 4000,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Agent konnte nicht geöffnet werden'));
    } finally {
      setLinkPending(null);
    }
  };

  const portalLinkClasses =
    'inline-flex items-center gap-1 font-mono text-blue-600 hover:text-blue-700 hover:underline disabled:cursor-default disabled:opacity-50';
  const copyButtonClasses = 'shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base">{event.errorIdentifier}</span>
            <button
              type="button"
              onClick={() => {
                void copyToClipboard(event.errorIdentifier, 'Fehler-Identifier');
              }}
              title="Fehler-Identifier kopieren"
              className={copyButtonClasses}
            >
              <Copy className="h-4 w-4" />
            </button>
            <SeverityBadge severity={event.severity} />
            <StatusBadge status={event.status} />
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {event.service} · {formatDateTime(event.occurredAt)}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <span className="text-gray-500">Kunde: </span>
            {event.customerId ? (
              <>
                {customerName && <span className="font-medium">{customerName}</span>}
                <div className="mt-0.5 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      void handleOpenCustomerPortal();
                    }}
                    disabled={linkPending !== null}
                    title="Customer-Portal als dieser Kunde öffnen (Impersonation, neuer Tab)"
                    className={portalLinkClasses}
                  >
                    {event.customerId}
                    {linkPending === 'customer' ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ExternalLink className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void copyToClipboard(event.customerId ?? '', 'Kunden-UUID');
                    }}
                    title="Kunden-UUID kopieren"
                    className={copyButtonClasses}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </>
            ) : (
              <span className="italic text-gray-400">nicht zuordenbar</span>
            )}
          </div>
          <div>
            <span className="text-gray-500">Agent: </span>
            {event.agentId ? (
              <>
                {agentIdentity && <span className="font-medium">{agentIdentity.name}</span>}
                <div className="mt-0.5 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      void handleOpenAgentPortal();
                    }}
                    disabled={linkPending !== null}
                    title="Agent im Customer-Portal öffnen (Impersonation, neuer Tab)"
                    className={portalLinkClasses}
                  >
                    {event.agentId}
                    {linkPending === 'agent' ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ExternalLink className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void copyToClipboard(event.agentId ?? '', 'Agent-UUID');
                    }}
                    title="Agent-UUID kopieren"
                    className={copyButtonClasses}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </>
            ) : (
              <span className="text-gray-400">–</span>
            )}
          </div>
          <div>
            <span className="text-gray-500">Session: </span>
            {event.sessionId ? (
              <span className="font-mono">{event.sessionId}</span>
            ) : (
              <span className="text-gray-400">–</span>
            )}
          </div>
          <div>
            <span className="text-gray-500">Error Source: </span>
            {event.errorSource ? <span>{event.errorSource}</span> : <span className="text-gray-400">–</span>}
          </div>
          <div>
            <span className="text-gray-500">Aufgetreten: </span>
            {formatDateTime(event.occurredAt)}
          </div>
          <div>
            <span className="text-gray-500">Gespeichert: </span>
            {formatDateTime(event.createdAt)}
          </div>
          {event.acknowledgedAt && (
            <div>
              <span className="text-gray-500">Quittiert von: </span>
              {event.acknowledgedBy ?? '–'}
            </div>
          )}
          {event.acknowledgedAt && (
            <div>
              <span className="text-gray-500">Quittiert am: </span>
              {formatDateTime(event.acknowledgedAt)}
            </div>
          )}
          {event.resolvedAt && (
            <div>
              <span className="text-gray-500">Gelöst von: </span>
              {event.resolvedBy ?? '–'}
            </div>
          )}
          {event.resolvedAt && (
            <div>
              <span className="text-gray-500">Gelöst am: </span>
              {formatDateTime(event.resolvedAt)}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-center gap-1">
              <p className="text-xs font-medium text-gray-500">Error</p>
              <button
                type="button"
                onClick={() => {
                  void copyToClipboard(event.error, 'Error');
                }}
                title="Error kopieren"
                className={copyButtonClasses}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="rounded border bg-gray-50 p-3 font-mono text-sm text-red-700">{event.error}</div>
          </div>
          <div>
            <div className="mb-1 flex items-center gap-1">
              <p className="text-xs font-medium text-gray-500">Info</p>
              <button
                type="button"
                onClick={() => {
                  void copyToClipboard(event.info, 'Info');
                }}
                title="Info kopieren"
                className={copyButtonClasses}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="rounded border bg-gray-50 p-3 text-sm">{event.info}</div>
          </div>
          <div>
            <div className="mb-1 flex items-center gap-1">
              <p className="text-xs font-medium text-gray-500">Help</p>
              <button
                type="button"
                onClick={() => {
                  void copyToClipboard(event.help, 'Help');
                }}
                title="Help kopieren"
                className={copyButtonClasses}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="rounded border bg-gray-50 p-3 text-sm">{event.help}</div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            disabled={event.status !== 'open' || actionPending}
            onClick={() => onAcknowledge(event)}
          >
            Quittieren
          </Button>
          <Button disabled={event.status === 'resolved' || actionPending} onClick={() => onResolve(event)}>
            Als gelöst markieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
